import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MuiTextField from '@mui/material/TextField';
import { Button, TextField } from 'cfa-react-components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import styles from './InvitationModal.module.scss';
import toastPromise from '../../../helpers/toast-promise';
import {
  usePostProjectInvitationMutation,
  usePostVendorInvitationMutation
} from '../../../store/invitationsApi';
import { useGetVendorsQuery } from '../../../store/vendorsApi';

const pendingHandler = () => 'Sending invitation...';

const successHandler = () =>
  'An invitation has been sent to the vendor at the requested email address.';

const requestAndNotify = toastPromise(pendingHandler, successHandler);

const InvitationModal = ({ showModal, handleClose, projectInvite }) => {
  const [postVendorInvitation] = usePostVendorInvitationMutation();
  const [postProjectInvitation] = usePostProjectInvitationMutation();
  const { data: vendors = [] } = useGetVendorsQuery(undefined, {
    skip: !!projectInvite
  });

  const validationSchema = projectInvite
    ? yup.object({
        email: yup.string().email('Invalid email address').required('Required')
      })
    : yup.object({
        email: yup.string().email('Invalid email address').required('Required'),
        vendor: yup.object().nullable().required('Required')
      });

  const initialValues = projectInvite
    ? { email: '' }
    : { email: '', vendor: null };

  const onFormikSubmit = async (values, { resetForm }) => {
    let response;

    const invitationRequest = projectInvite
      ? postProjectInvitation({
          vendorId: projectInvite.vendorId,
          projectId: projectInvite.projectId,
          values
        })
      : postVendorInvitation({
          values: { email: values.email },
          vendorBridgeId: values.vendor?.id
        });

    try {
      response = await requestAndNotify(invitationRequest.unwrap());
    } catch (error) {}

    if (response?.status === 200 || response === '') {
      handleClose();
      resetForm();
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: onFormikSubmit
  });

  const dialogTitle = projectInvite
    ? 'Project Invitation'
    : 'Vendor Invitation';

  const dialogContextText = projectInvite
    ? 'To notify user of project access, please enter the contact email address here.'
    : 'To notify vendor of onboarding portal access, please select the vendor and enter the contact email address here.';

  return (
    <div className={styles['page-centered']}>
      <Dialog open={showModal} onClose={handleClose}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <DialogContentText>{dialogContextText}</DialogContentText>
            {!projectInvite && (
              <div className={styles['centered-container']}>
                <Autocomplete
                  fullWidth
                  id="vendor"
                  options={vendors}
                  value={formik.values.vendor}
                  onChange={(_event, value) =>
                    formik.setFieldValue('vendor', value)
                  }
                  onBlur={() => formik.setFieldTouched('vendor', true)}
                  getOptionLabel={(v) => v?.name ?? ''}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Vendor"
                      error={
                        formik.touched.vendor && Boolean(formik.errors.vendor)
                      }
                      helperText={formik.touched.vendor && formik.errors.vendor}
                    />
                  )}
                />
              </div>
            )}
            <div className={styles['centered-container']}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>
            <div className={styles['centered-container']}>
              <div>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </div>
              <div>
                <Button type="submit" color="primary" size="small">
                  Submit
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvitationModal;
